import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { fetchSections } from "../lib/api";
import { Search, Phone, Calculator, Map, BookOpen, ShieldAlert, ScrollText, GraduationCap, Sparkles } from "lucide-react";

const ICONS = {
    "Introduction": BookOpen,
    "Emergency": ShieldAlert,
    "Codes & Conduct": ScrollText,
    "Requirements": ScrollText,
    "Procedures": ScrollText,
    "Safety": ShieldAlert,
    "Policies": ScrollText,
    "Practical": BookOpen,
    "Training": GraduationCap,
    "About": Sparkles,
};

export default function Sidebar({ onNavigate }) {
    const [sections, setSections] = useState([]);
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        fetchSections().then(setSections).catch(() => {});
    }, []);

    const grouped = useMemo(() => {
        const g = {};
        sections.forEach(s => {
            if (!g[s.category]) g[s.category] = [];
            g[s.category].push(s);
        });
        return g;
    }, [sections]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (query.trim()) {
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
            onNavigate?.();
        }
    };

    const linkBase = "flex items-center gap-2 px-3 py-2 rounded-md text-[15px] transition-colors";
    const linkActive = "bg-[#2C7A85] text-white";
    const linkInactive = "text-gray-700 hover:bg-gray-100";

    const tools = [
        { to: "/emergency", label: "Emergency Contacts", icon: Phone },
        { to: "/fare", label: "Fare Calculator", icon: Calculator },
        { to: "/mileage", label: "Mileage Chart", icon: Map },
    ];

    return (
        <aside className="h-full overflow-y-auto py-6 pr-2 pl-4 lg:pl-6" data-testid="sidebar">
            <form onSubmit={handleSearch} className="mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search the handbook…"
                        className="w-full pl-9 pr-3 py-2.5 text-[15px] bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2C7A85] focus:border-transparent outline-none"
                        data-testid="sidebar-search-input"
                    />
                </div>
            </form>

            <div className="mb-7">
                <div className="text-[11px] uppercase tracking-widest text-gray-500 px-3 mb-2 font-semibold">Quick Tools</div>
                <nav className="space-y-1">
                    {tools.map(t => {
                        const Icon = t.icon;
                        return (
                            <NavLink
                                key={t.to}
                                to={t.to}
                                onClick={onNavigate}
                                data-testid={`sidebar-tool-${t.to.replace("/", "")}`}
                                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                            >
                                <Icon className="h-4 w-4 flex-shrink-0" />
                                <span>{t.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>
            </div>

            {Object.entries(grouped).map(([cat, items]) => {
                const Icon = ICONS[cat] || BookOpen;
                return (
                    <div key={cat} className="mb-6">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 px-3 mb-2 font-semibold">
                            <Icon className="h-3 w-3" />
                            {cat}
                        </div>
                        <nav className="space-y-0.5">
                            {items.map(s => (
                                <NavLink
                                    key={s.id}
                                    to={`/section/${s.id}`}
                                    onClick={onNavigate}
                                    data-testid={`sidebar-section-${s.id}`}
                                    className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                                >
                                    <span className="leading-snug">{s.title}</span>
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                );
            })}
        </aside>
    );
}
